import React from "react";
import { motion } from "framer-motion";
import { Ruler, TrendingUp, ShieldCheck, Target } from "lucide-react";
import { homeData } from "../../data/homeData";

/* ── Container animation  staggered children ── */
const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2,
    },
  },
};

/* ── Individual card entrance ── */
const cardVariants = {
  hidden: { opacity: 0, y: 40, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.7,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

/* ── Heading entrance ── */
const headingVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.9,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const HomeStats = () => {
  const { stats } = homeData;

  const styles = [
    {
      bg: "bg-[#e1eeff]",
      hoverBg: "group-hover:bg-blue-600",
      text: "text-blue-600",
      hoverText: "group-hover:text-white",
      accent: "#3b82f6",
      Icon: ShieldCheck,
    },
    {
      bg: "bg-[#ffe1e1]",
      hoverBg: "group-hover:bg-red-500",
      text: "text-red-500",
      hoverText: "group-hover:text-white",
      accent: "#ef4444",
      Icon: TrendingUp,
    },
    {
      bg: "bg-[#e1f5e1]",
      hoverBg: "group-hover:bg-green-600",
      text: "text-green-600",
      hoverText: "group-hover:text-white",
      accent: "#16a34a",
      Icon: Target,
    },
    {
      bg: "bg-[#fff3cc]",
      hoverBg: "group-hover:bg-amber-500",
      text: "text-amber-500",
      hoverText: "group-hover:text-white",
      accent: "#f59e0b",
      Icon: Ruler,
    },
  ];

  return (
    <section className="relative z-20 w-full bg-[#ECF0F4] py-16 md:py-24 md:-mt-24 ">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Section heading with blur-in animation */}
        <motion.div
          variants={headingVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          className="mb-14"
        >
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-slate-800 text-center">
            Explore our Modern Ecosystem
          </h2>
        </motion.div>

        {/* Cards grid with staggered entrance */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8"
        >
          {stats.map((stat, i) => {
            const config = styles[i % styles.length];
            const Icon = config.Icon;

            return (
              <motion.div
                variants={cardVariants}
                key={i}
                whileHover={{ y: -4, transition: { duration: 0.3 } }}
                className="group relative flex flex-col p-8 rounded-[0.5rem] bg-white border border-slate-100 
                           cursor-pointer overflow-hidden
                           transition-shadow duration-500 ease-out
                           hover:shadow-[0_20px_60px_rgba(0,0,0,0.08)]
                           will-change-[transform,opacity]"
              >
                {/* Hover background fill  expands from icon position */}
                <div
                  className="absolute inset-0 rounded-[0.5rem] opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{
                    background: `linear-gradient(135deg, ${config.accent}10 0%, ${config.accent}08 100%)`,
                  }}
                />

                {/* Accent top bar  reveals on hover */}
                <div
                  className="absolute top-0 left-0 right-0 h-1 rounded-t-[1.5rem] scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"
                  style={{ backgroundColor: config.accent }}
                />

                {/* Icon Circle  with hover color shift */}
                <motion.div
                  whileHover={{ rotate: [0, -10, 10, 0] }}
                  transition={{ duration: 0.5 }}
                  className={`relative z-10 h-14 w-14 rounded-2xl ${config.bg} ${config.hoverBg} ${config.text} ${config.hoverText}
                             flex items-center justify-center mb-6 
                             transition-all duration-500 ease-out
                             shadow-sm group-hover:shadow-lg group-hover:scale-110`}
                >
                  <Icon size={22} strokeWidth={2} />
                </motion.div>

                {/* Value  with count-up feel via scale */}
                <div className="relative z-10 mb-3">
                  <h3 className="font-bold text-[1.4rem] text-slate-900 leading-tight group-hover:text-slate-800 transition-colors duration-300">
                    {stat.value}
                  </h3>
                </div>

                {/* Description */}
                <p className="relative z-10 text-[14px] text-slate-500 font-medium leading-relaxed group-hover:text-slate-700 transition-colors duration-300">
                  {stat.label}
                </p>

                {/* Bottom corner decoration  appears on hover */}
                <div
                  className="absolute bottom-0 right-0 w-24 h-24 rounded-tl-full opacity-0 group-hover:opacity-100 transition-all duration-700"
                  style={{
                    background: `radial-gradient(circle at bottom right, ${config.accent}15, transparent 70%)`,
                  }}
                />
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default HomeStats;
